/**
 * Standard modal overlay + container.
 */
import React from 'react';
import { surfaces, zIndex, cn } from './uiTokens';

type ModalVariant = 'center' | 'slideRight' | 'fullscreen';

interface ModalShellProps {
  children: React.ReactNode;
  onClose?: () => void;
  variant?: ModalVariant;
  zLevel?: keyof typeof zIndex;
  className?: string;
  overlayClassName?: string;
  testId?: string;
  /** Click overlay to close */
  dismissOnOverlay?: boolean;
}

const zClass: Record<keyof typeof zIndex, string> = {
  hud: 'z-10',
  hudPanel: 'z-30',
  inspector: 'z-40',
  modal: 'z-50',
  modalHigh: 'z-[60]',
  menu: 'z-[70]',
};

const ModalShell: React.FC<ModalShellProps> = ({
  children,
  onClose,
  variant = 'center',
  zLevel = 'modal',
  className,
  overlayClassName,
  testId,
  dismissOnOverlay = false,
}) => {
  const overlay = cn(
    'absolute inset-0 pointer-events-auto flex',
    surfaces.overlay,
    zClass[zLevel],
    variant === 'slideRight' ? 'items-stretch justify-end' : 'items-center justify-center p-2 md:p-4',
    overlayClassName,
  );

  const handleOverlayClick = () => {
    if (dismissOnOverlay && onClose) onClose();
  };

  return (
    <div className={overlay} data-testid={testId} onClick={dismissOnOverlay ? handleOverlayClick : undefined}>
      <div
        className={cn(
          variant === 'slideRight' && 'w-full max-w-md h-full animate-slide-in-right',
          variant === 'center' && 'w-full animate-fade-in',
          variant === 'fullscreen' && 'w-full h-full',
          className,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
};

export default ModalShell;